/**
 * <coming-soon-notify> — quiet "notify me when it drops" waitlist form.
 *
 * On submit it makes client-side Klaviyo API calls (no libraries):
 *   1. subscribes the email to the drop list (company_id = public key), then, when
 *      the optional phone field is filled in, the number to SMS as a separate subscription
 *   2. appends the form's waitlist tag (data-waitlist-tag) to the profile's
 *      "Drop waitlists" list property, so each drop's signups can be segmented
 *   3. fires a "Subscribed to Drop" event per product or per tagged drop, so each
 *      waitlist keeps its own history (plain profile properties would overwrite)
 *
 * Config comes from data attributes set by snippets/coming-soon-notify.liquid.
 * A filled honeypot silently no-ops as a success to deter bots.
 */
// Scoped so a page can include the form (and this script) more than once.
(() => {
  if (customElements.get('coming-soon-notify')) return;

  const KLAVIYO_REVISION = '2025-04-15';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const E164_RE = /^\+[1-9]\d{7,14}$/;
  const WAITLIST_PROPERTY = 'Drop waitlists';

  // Australian mobiles may be typed as 0412 345 678, 412 345 678 or 61 412 345 678;
  // anything else needs its + country code. Returns '' when blank, null when invalid.
  const toE164 = (raw) => {
    let phone = (raw || '').replace(/[\s().-]/g, '');
    if (!phone) return '';
    if (/^\+610/.test(phone)) phone = `+61${phone.slice(4)}`;
    else if (/^0[45]\d{8}$/.test(phone)) phone = `+61${phone.slice(1)}`;
    else if (/^[45]\d{8}$/.test(phone)) phone = `+61${phone}`;
    else if (/^61[45]\d{8}$/.test(phone)) phone = `+${phone}`;
    return E164_RE.test(phone) ? phone : null;
  };

  class ComingSoonNotify extends HTMLElement {
    connectedCallback() {
      this.publicKey = this.dataset.publicKey;
      this.listId = this.dataset.listId;
      if (!this.publicKey || !this.listId) return;

      this.form = this.querySelector('.js-coming-soon-form');
      this.emailInput = this.querySelector('.js-coming-soon-email');
      this.phoneInput = this.querySelector('.js-coming-soon-phone');
      this.honeypot = this.querySelector('.js-coming-soon-hp');
      this.submitBtn = this.querySelector('.js-coming-soon-submit');
      this.message = this.querySelector('.js-coming-soon-message');
      this.dismissBtn = this.querySelector('.js-coming-soon-dismiss');
      if (!this.form) return;

      if (this.bound) return;
      this.bound = true;
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      if (this.dismissBtn) {
        this.dismissBtn.addEventListener('click', () => {
          this.dispatchEvent(new CustomEvent('coming-soon-notify:dismiss', { bubbles: true }));
        });
      }
    }

    // Back to the empty form, for a form that is reused across products.
    reset() {
      if (!this.form) return;
      this.form.reset();
      this.form.querySelectorAll('.coming-soon-notify__row, .coming-soon-notify__phone-note').forEach((row) => {
        row.hidden = false;
      });
      if (this.dismissBtn) this.dismissBtn.hidden = false;
      if (this.message) {
        this.message.textContent = '';
        this.message.classList.remove('coming-soon-notify__message--success', 'coming-soon-notify__message--error');
      }
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.pending) return;

      if (this.honeypot && this.honeypot.value) {
        this.showMessage('success');
        return;
      }

      const email = (this.emailInput.value || '').trim();
      if (!EMAIL_RE.test(email)) {
        this.emailInput.focus();
        this.showMessage(this.message && this.message.dataset.labelInvalid ? 'invalid' : 'error');
        return;
      }

      const phone = this.phoneInput ? toE164(this.phoneInput.value) : '';
      if (phone === null) {
        this.phoneInput.focus();
        this.showMessage('phoneInvalid');
        return;
      }

      this.setPending(true);
      // Email first, on its own. Klaviyo accepts subscriptions (202) and processes them later,
      // so a number it rejects then (e.g. already on another profile) would silently drop an
      // email sent in the same request. SMS follows as a separate subscription.
      this.subscribe(email, '')
        .then(() => {
          // Success reflects the email subscription only — SMS, the tag and the event are
          // fire-and-forget so a failed/blocked call can't mask a completed subscribe.
          if (phone) this.subscribe(email, phone).catch(() => {});
          this.tagProfile(email).catch(() => {});
          this.trackEvent(email).catch(() => {});
          this.showMessage('success');
          this.form.reset();
        })
        .catch(() => this.showMessage('error'))
        .finally(() => this.setPending(false));
    }

    subscribe(email, phone) {
      const attributes = {
        email: email,
        subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
      };
      if (phone) {
        attributes.phone_number = phone;
        attributes.subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
      }
      const body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Coming Soon Waitlist',
            profile: {
              data: {
                type: 'profile',
                attributes: attributes,
              },
            },
          },
          relationships: {
            list: { data: { type: 'list', id: this.listId } },
          },
        },
      };
      return this.post('client/subscriptions', body);
    }

    // Adds the drop to the profile's list of waitlists without overwriting earlier drops.
    tagProfile(email) {
      const tag = this.dataset.waitlistTag;
      if (!tag) return Promise.resolve();
      const body = {
        data: {
          type: 'profile',
          attributes: { email: email },
          meta: { patch_properties: { append: { [WAITLIST_PROPERTY]: tag } } },
        },
      };
      return this.post('client/profiles', body);
    }

    trackEvent(email) {
      // The event needs a product or a waitlist tag; an untagged drop-level form only
      // joins the list.
      const tag = this.dataset.waitlistTag;
      if (!this.dataset.productHandle && !tag) return Promise.resolve();
      const properties = {};
      if (tag) properties.drop = tag;
      if (this.dataset.productHandle) {
        properties.drop_product_handle = this.dataset.productHandle;
        properties.drop_product_title = this.dataset.productTitle;
        properties.drop_product_url = this.dataset.productUrl;
      }
      if (this.dataset.dropAt) properties.drop_at = this.dataset.dropAt;

      const body = {
        data: {
          type: 'event',
          attributes: {
            properties: properties,
            metric: { data: { type: 'metric', attributes: { name: 'Subscribed to Drop' } } },
            profile: { data: { type: 'profile', attributes: { email: email } } },
          },
        },
      };
      return this.post('client/events', body);
    }

    post(path, body) {
      return fetch(`https://a.klaviyo.com/${path}/?company_id=${encodeURIComponent(this.publicKey)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify(body),
      }).then((response) => {
        if (!response.ok) throw new Error(`Klaviyo ${path} responded ${response.status}`);
        return response;
      });
    }

    setPending(pending) {
      this.pending = pending;
      if (!this.submitBtn) return;
      this.submitBtn.disabled = pending;
      const label = pending ? this.submitBtn.dataset.labelPending : this.submitBtn.dataset.labelDefault;
      if (label) this.submitBtn.textContent = label;
    }

    showMessage(state) {
      if (!this.message) return;
      const labels = {
        success: this.message.dataset.labelSuccess,
        invalid: this.message.dataset.labelInvalid,
        phoneInvalid: this.message.dataset.labelPhoneInvalid,
        error: this.message.dataset.labelError,
      };
      // The status node is always in the DOM (empty) so aria-live announces the
      // text swap; we never toggle hidden in the same tick as setting the text.
      this.message.textContent = labels[state];
      this.message.classList.toggle('coming-soon-notify__message--success', state === 'success');
      this.message.classList.toggle('coming-soon-notify__message--error', state !== 'success');
      if (state === 'success') {
        if (this.form) {
          this.form.querySelectorAll('.coming-soon-notify__row, .coming-soon-notify__phone-note').forEach((row) => {
            row.hidden = true;
          });
        }
        if (this.dismissBtn) this.dismissBtn.hidden = true;
        this.message.focus();
      }
    }
  }

  customElements.define('coming-soon-notify', ComingSoonNotify);
})();
